import ChatMessageApi, { ApiMessage, MessageFeedbackValue } from '@api/agentBuilder/chatmessage'
import { PermissionErrorDetails } from '@api/generated/public/publicApi'
import { useAgentApi } from '@api/hooks'
import { PredictionSSE, useSessionSSE } from '@api/hooks/useSessionSSE'
import { PromptBoxForm } from '@components/PromptBox'
import {
  MESSAGE_TYPE_API,
  MESSAGE_TYPE_FINAL_ANSWER,
  MESSAGE_TYPE_FINAL_ANSWER_STREAM,
  MESSAGE_TYPE_LLM_ERROR,
  MESSAGE_TYPE_SYSTEM,
  MESSAGE_TYPE_USER_INPUT,
} from '@constants/agentTypes'
import { eventTypes } from '@constants/analytics'
import {
  ERROR_MESSAGE_CLIENT_ERROR,
  ERROR_MESSAGE_GENERAL,
  ERROR_MESSAGE_INTERNAL_SERVER,
  ERROR_MESSAGE_SERVICE_UNAVAILABLE,
  ERROR_MESSAGE_UNEXPECTED,
} from '@constants/errorMessages'
import useApiClient from '@hooks/useApiClient'
import {
  WebsocketMessageData as InferenceMessageData,
  InternalMessage,
  QueuedMessageUpdate,
  SubTeammateStructuredOutput,
} from '@slices/userMessage'
import {
  describeApiErrorResponse,
  getApiErrorResponse,
  getPermissionDeniedBody,
  getUserFacingMessage,
  isNetworkError,
} from '@/utils/apiErrorResponse'
import axios from 'axios'
import { isEmpty } from 'lodash'
import { useCallback, useEffect, useReducer, useRef } from 'react'
import toast from 'react-hot-toast'
import { useSearchParams } from 'react-router-dom'
import useSWR, { useSWRConfig } from 'swr'
import { useInferenceMessageContext } from './context'
import { parseInferenceSSEEvent } from './events'
import { apiMessageToInternalMessage, getToolCallContent } from './messageConversion'
import { createInitialInferenceState, inferenceReducer, selectInferenceLoading } from './state'
import { isStaleBatch, storeHoldsAnotherSession } from './sessionMerge'

const SWR_CHAT_MESSAGE_KEY = 'chat-message'
const STREAM_TOKEN_FLUSH_MS = 50

const AGENT_CONNECTED_MESSAGE: InternalMessage = {
  type: MESSAGE_TYPE_SYSTEM,
  value: 'Connection established, submit a new message to start.',
}

type MissingResources = PermissionErrorDetails['missingResources']
type SubmitMessageErrorHandler = (missingResources?: MissingResources) => void

type StreamTokenFlushHandle = { type: 'animationFrame'; id: number } | { type: 'timeout'; id: ReturnType<typeof setTimeout> }

export type SubmitMessageFeedbackRequest = {
  messageId: string
  text?: string | null
  value?: MessageFeedbackValue | null
}

const SWR_SESSION_FILES_KEY = 'session-files'

type UseInferenceOptions = {
  resetMessagesOnUnmount?: boolean
}

export default function useInference(
  chatflowId: string,
  onError: SubmitMessageErrorHandler,
  sessionId?: string,
  { resetMessagesOnUnmount = true }: UseInferenceOptions = {}
) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [state, dispatchInference] = useReducer(inferenceReducer, sessionId, createInitialInferenceState)
  const { currentSessionId, latestMessage, videoEnabled, videoLink, videoLoading } = state
  const {
    messages,
    resetMessages,
    addMessage,
    addStreamMessage,
    updateStreamMessage,
    updateQueuedMessage,
    markToolCallPendingApproval,
    linkSubTeammateChildSession,
    setSubTeammateAcceptanceState,
    clearSubTeammateAcceptanceState,
    markSubTeammateRerunning,
    setMessages,
  } = useInferenceMessageContext()
  const loading = selectInferenceLoading(state, messages)

  useEffect(() => {
    dispatchInference({ type: 'setCurrentSessionId', sessionId })
  }, [sessionId])

  useEffect(() => {
    return () => {
      if (resetMessagesOnUnmount) {
        resetMessages()
      }
    }
  }, [resetMessages, resetMessagesOnUnmount])

  // The session whose messages the shared message store currently holds. Distinct from
  // `currentSessionId`, which tracks the requested session and moves ahead of the loaded data.
  const storeSessionIdRef = useRef<string | undefined>(sessionId)

  const clearMessages = () => setMessages([])
  const streamTokenBufferRef = useRef<{ token: string; data: InferenceMessageData } | null>(null)
  const streamTokenFlushHandleRef = useRef<StreamTokenFlushHandle | null>(null)

  const { mutate } = useSWRConfig()
  const apiClientContext = useApiClient()
  const chatMessageApi = new ChatMessageApi(apiClientContext.torqAgentClient)
  const agentApi = useAgentApi()

  const {
    data: messagesFromApi,
    mutate: refetchMessages,
    isLoading: isLoadingMessages,
  } = useSWR([SWR_CHAT_MESSAGE_KEY, chatflowId, sessionId ?? currentSessionId], async () => {
    const res = await chatMessageApi.getChatmessageFromChatflow(chatflowId, sessionId ?? currentSessionId)
    return res.data
  })

  useEffect(() => {
    let effectiveSessionId = sessionId
    if (!effectiveSessionId && messagesFromApi && messagesFromApi.length > 0) {
      effectiveSessionId = messagesFromApi[0].sessionId
    }
    const differentSession = effectiveSessionId !== currentSessionId

    if (!messagesFromApi) return

    // While SWR revalidates after a session switch, messagesFromApi can still hold the PREVIOUS
    // session's batch. Merging it would write those messages into the new session's view, and they
    // survive the de-duplication below because they match none of the new session's ids or values.
    // An explicit sessionId is authoritative, so drop any batch that does not belong to it.
    if (isStaleBatch(sessionId, messagesFromApi[0]?.sessionId)) return

    if (differentSession) {
      dispatchInference({ type: 'setCurrentSessionId', sessionId: effectiveSessionId })
    }

    const apiMessages = messagesFromApi.map(apiMessageToInternalMessage)
    const apiMessageById = new Map(apiMessages.filter(m => m.id).map(m => [m.id, m]))
    const apiUserInputValues = new Set(apiMessages.filter(m => m.type === MESSAGE_TYPE_USER_INPUT).map(m => m.value))
    const apiFinalAnswerValues = new Set(apiMessages.filter(m => m.type === MESSAGE_TYPE_FINAL_ANSWER).map(m => m.value))

    // Keep local-only messages only when the store already holds THIS session. `currentSessionId`
    // cannot answer that: the effect above advances it as soon as the sessionId prop changes, so by
    // the time a batch arrives `differentSession` is already false and the store is never cleared.
    // Track the session the store actually holds instead.
    const storeMessages = storeHoldsAnotherSession(storeSessionIdRef.current, effectiveSessionId) ? [] : messages
    storeSessionIdRef.current = effectiveSessionId

    const storeOnlyMessages = storeMessages.filter(m => {
      if (m.id && apiMessageById.has(m.id)) return false
      if (!m.id && m.type === MESSAGE_TYPE_USER_INPUT && apiUserInputValues.has(m.value)) return false
      if ((m.type === MESSAGE_TYPE_FINAL_ANSWER_STREAM || m.type === MESSAGE_TYPE_FINAL_ANSWER) && apiFinalAnswerValues.has(m.value))
        return false
      return true
    })

    const newMessages = [...apiMessages, ...storeOnlyMessages]

    setMessages(newMessages)

    updateLatestMessageFromMessages(messagesFromApi)

    // Including messages here loops because this effect reconciles API messages into local state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesFromApi, setMessages, isLoadingMessages])

  const updateLatestMessageFromMessages = (data: ApiMessage[]) => {
    const userInputAndFinalAnswers = data.filter((m: ApiMessage) => {
      return [MESSAGE_TYPE_USER_INPUT, MESSAGE_TYPE_FINAL_ANSWER].includes(m.role)
    })
    if (!isEmpty(userInputAndFinalAnswers)) {
      if (
        userInputAndFinalAnswers.slice(-1)[0].role === MESSAGE_TYPE_FINAL_ANSWER &&
        userInputAndFinalAnswers.slice(-2)[0].role === MESSAGE_TYPE_USER_INPUT
      ) {
        dispatchInference({
          type: 'setLatestMessage',
          latestMessage: {
            userInput: userInputAndFinalAnswers.slice(-2)[0].content,
            response: userInputAndFinalAnswers.slice(-1)[0].content,
          },
        })
      }
    }
  }

  const handleError = (message = ERROR_MESSAGE_GENERAL) => {
    message = message.replace(`Unable to parse JSON response from chat agent.\n\n`, '')
    addMessage({ value: message, type: MESSAGE_TYPE_API })
    dispatchInference({ type: 'setSubmitting', submitting: false })
  }

  const appendMessage = useCallback(
    (message: any) => {
      if (message.type === 'toolCallPendingApproval') {
        markToolCallPendingApproval(message)
        return
      }

      if (message.type === 'toolCallPendingBackground' && message.pendingBackground && message.id && message.data?.childSessionId) {
        linkSubTeammateChildSession(message.id, message.data.childSessionId, message.data.childChatflowName)
        return
      }

      if (message.type === 'subTeammatePendingAcceptance' && message.id && message.structuredOutput) {
        setSubTeammateAcceptanceState({
          parentToolCallMessageId: message.id,
          pendingUserAcceptance: true,
          structuredOutput: message.structuredOutput as SubTeammateStructuredOutput,
        })
        dispatchInference({ type: 'setSubmitting', submitting: false })
        return
      }

      if (
        message.type === MESSAGE_TYPE_FINAL_ANSWER ||
        message.type === MESSAGE_TYPE_FINAL_ANSWER_STREAM ||
        message.type === MESSAGE_TYPE_LLM_ERROR
      ) {
        dispatchInference({ type: 'setSubmitting', submitting: false })
      }

      if (message.status === 'failed' && message.type === 'task') {
        console.error('[sse] [failed]:', message.value ?? message)
        return
      }

      if (isEmpty(message.value)) {
        return
      }
      addMessage(message)
    },
    [addMessage, markToolCallPendingApproval, linkSubTeammateChildSession, setSubTeammateAcceptanceState]
  )

  const updateFiles = useCallback(
    ({ files }: { files: { name: string; url: string }[] }) => {
      if (currentSessionId) {
        const filesAsApiFormat = files.map(f => ({ fileName: f.name, url: f.url }))
        void mutate([SWR_SESSION_FILES_KEY, currentSessionId], filesAsApiFormat, { revalidate: false })
      }
    },
    [currentSessionId, mutate]
  )

  const cancelScheduledStreamTokenFlush = useCallback(() => {
    const handle = streamTokenFlushHandleRef.current
    if (!handle) return

    if (handle.type === 'animationFrame') {
      window.cancelAnimationFrame(handle.id)
    } else {
      clearTimeout(handle.id)
    }
    streamTokenFlushHandleRef.current = null
  }, [])

  const flushStreamTokens = useCallback(() => {
    cancelScheduledStreamTokenFlush()

    const buffered = streamTokenBufferRef.current
    if (!buffered) return

    streamTokenBufferRef.current = null
    updateStreamMessage(buffered)
  }, [cancelScheduledStreamTokenFlush, updateStreamMessage])

  const scheduleStreamTokenFlush = useCallback(() => {
    if (streamTokenFlushHandleRef.current) return

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      streamTokenFlushHandleRef.current = {
        type: 'animationFrame',
        id: window.requestAnimationFrame(flushStreamTokens),
      }
      return
    }

    streamTokenFlushHandleRef.current = {
      type: 'timeout',
      id: setTimeout(flushStreamTokens, STREAM_TOKEN_FLUSH_MS),
    }
  }, [flushStreamTokens])

  const queueStreamToken = useCallback(
    ({ token, data }: { token: string; data: InferenceMessageData }) => {
      const buffered = streamTokenBufferRef.current
      const sameStream =
        buffered?.data?.requestMessageId === data?.requestMessageId &&
        buffered?.data?.parentRunId === data?.parentRunId &&
        buffered?.data?.runId === data?.runId

      if (buffered && !sameStream) {
        flushStreamTokens()
      }

      streamTokenBufferRef.current = {
        token: `${streamTokenBufferRef.current?.token ?? ''}${token}`,
        data,
      }
      scheduleStreamTokenFlush()
    },
    [flushStreamTokens, scheduleStreamTokenFlush]
  )

  useEffect(() => {
    return () => {
      cancelScheduledStreamTokenFlush()
    }
  }, [cancelScheduledStreamTokenFlush])

  const handleSSEEvent = useCallback(
    (event: PredictionSSE) => {
      const parsedEvent = parseInferenceSSEEvent(event)

      switch (parsedEvent.type) {
        case 'connected':
          addMessage(AGENT_CONNECTED_MESSAGE)
          break

        case 'streamStart':
          flushStreamTokens()
          dispatchInference({ type: 'setStreaming' })
          addStreamMessage(parsedEvent.message)
          break

        case 'streamToken':
          queueStreamToken({ token: parsedEvent.token, data: parsedEvent.data })
          break

        case 'message':
          flushStreamTokens()
          appendMessage(parsedEvent.message)
          break

        case 'files':
          flushStreamTokens()
          updateFiles(parsedEvent.data)
          break

        case 'videoLink':
          flushStreamTokens()
          if (parsedEvent.url) dispatchInference({ type: 'setVideoLink', videoLink: parsedEvent.url })
          break

        case 'videoError':
          flushStreamTokens()
          dispatchInference({ type: 'setVideoLoading', loading: false })
          toast.error(parsedEvent.message ?? 'Video generation failed')
          break

        case 'messageStatus':
          updateQueuedMessage(parsedEvent.update)
          if (['completed', 'failed', 'cancelled'].includes(parsedEvent.update.status)) {
            flushStreamTokens()
            dispatchInference({ type: 'setSubmitting', submitting: false })
            void refetchMessages()
          }
          break

        case 'sessionState': {
          // session:state is the SSE catch-up event; streamBuffer is not persisted yet.
          flushStreamTokens()
          parsedEvent.queuedMessages.forEach(updateQueuedMessage)
          if (parsedEvent.streamBuffer) {
            addStreamMessage({
              value: '',
              type: MESSAGE_TYPE_FINAL_ANSWER_STREAM,
              requestMessageId: parsedEvent.requestMessageId,
            })
            updateStreamMessage({
              token: parsedEvent.streamBuffer,
              data: { requestMessageId: parsedEvent.requestMessageId } as InferenceMessageData,
            })
          }
          void refetchMessages()
          break
        }

        case 'ignored':
          flushStreamTokens()
          break
      }
    },
    [
      addMessage,
      addStreamMessage,
      updateStreamMessage,
      updateQueuedMessage,
      queueStreamToken,
      flushStreamTokens,
      appendMessage,
      updateFiles,
      refetchMessages,
    ]
  )

  const handleSSEDisconnected = useCallback(() => {
    flushStreamTokens()
    dispatchInference({ type: 'setSubmitting', submitting: false })
  }, [flushStreamTokens])

  const sseSessionId = sessionId ?? currentSessionId

  const { connectionState } = useSessionSSE({
    chatflowId,
    sessionId: sseSessionId,
    onEvent: handleSSEEvent,
    onDisconnected: handleSSEDisconnected,
  })

  const isConnected = connectionState === 'connected'

  const sseSessionIdRef = useRef<string | undefined>(sseSessionId)
  const connectionStateRef = useRef(connectionState)
  useEffect(() => {
    sseSessionIdRef.current = sseSessionId
  }, [sseSessionId])
  useEffect(() => {
    connectionStateRef.current = connectionState
  }, [connectionState])

  const waitForSSEConnection = useCallback(async (targetSessionId: string, timeoutMs = 3000): Promise<void> => {
    const startTime = Date.now()
    while (sseSessionIdRef.current !== targetSessionId || connectionStateRef.current !== 'connected') {
      if (Date.now() - startTime > timeoutMs) {
        console.warn(`[SSE] Timed out waiting for connection to session ${targetSessionId}`)
        return
      }
      await new Promise(resolve => setTimeout(resolve, 25))
    }
  }, [])

  const updateSessionQueryParam = (sessionId: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('session', sessionId)
      return next
    })
  }

  const handleSubmitMessage = async (v: PromptBoxForm) => {
    dispatchInference({ type: 'setSubmitting', submitting: true })
    addMessage({
      value: v.prompt,
      type: MESSAGE_TYPE_USER_INPUT,
      chatFiles: v.files.map(f => ({ id: f.id ?? '', fileName: f.file?.name ?? '' })),
      queuedFiles: v.files.flatMap(file => (file.file ? [{ fileName: file.file.name, status: 'staging' }] : [])),
    })

    const newlyCreatedSessionId = await initializeSession(v.prompt).catch(err => {
      console.error('Error initializing session', err)
      return undefined
    })

    if (newlyCreatedSessionId) {
      await waitForSSEConnection(newlyCreatedSessionId)
    }

    window.analytics.track(eventTypes.SEND_PROMPT, {
      chatflowId,
    })

    if (videoEnabled) {
      dispatchInference({ type: 'setVideoLoading', loading: true })
    }

    try {
      const requestSessionId = sessionId ?? newlyCreatedSessionId ?? currentSessionId
      if (!requestSessionId) throw new Error('Unable to create a chat session')
      const response = await agentApi.chatflows.runQueuePrediction(
        chatflowId,
        {
          message: JSON.stringify({ question: v.prompt, videoEnabled }),
          files: v.files.flatMap(file => (file.file ? [file.file] : [])),
        },
        { sessionId: requestSessionId }
      )
      updateQueuedMessage(response.data as QueuedMessageUpdate)
    } catch (error: unknown) {
      // `agentApi` is the generated fetch client, which throws the augmented Response rather than an
      // AxiosError, so read the status through the normalizer that understands both shapes (WF-2745).
      const errorResponse = getApiErrorResponse(error)

      if (errorResponse) {
        if (errorResponse.status === 401 || errorResponse.status === 403) {
          dispatchInference({ type: 'setSubmitting', submitting: false })

          // A 403 from the prediction gate carries the providers the caller cannot use, so the
          // dialog can name them. Any other 401/403 has no such body and falls back to generic copy.
          const missingResources: MissingResources | undefined = getPermissionDeniedBody(errorResponse.body)?.details?.missingResources

          onError(missingResources)
        } else {
          console.error(describeApiErrorResponse(errorResponse))
          if (errorResponse.status === 500) {
            handleError(ERROR_MESSAGE_INTERNAL_SERVER)
          } else if (errorResponse.status > 500 && errorResponse.status <= 599) {
            handleError(ERROR_MESSAGE_SERVICE_UNAVAILABLE)
          } else if (errorResponse.status >= 400 && errorResponse.status <= 499) {
            const userFacingMessage = getUserFacingMessage(errorResponse.body)
            if (userFacingMessage) {
              handleError(userFacingMessage)
            } else {
              handleError(ERROR_MESSAGE_CLIENT_ERROR)
            }
          }
        }
      } else if (isNetworkError(error)) {
        handleError(ERROR_MESSAGE_SERVICE_UNAVAILABLE)
      } else {
        console.error(error)
        handleError(ERROR_MESSAGE_UNEXPECTED)
      }
    }

    await refetchMessages()

    const effectiveSessionIdForFiles = sessionId ?? currentSessionId
    if (effectiveSessionIdForFiles) {
      void mutate([SWR_SESSION_FILES_KEY, effectiveSessionIdForFiles])
    }
  }

  const initializeSession = async (prompt: string): Promise<string | undefined> => {
    const isNewSession = !messages.some(m => m.type === MESSAGE_TYPE_USER_INPUT)
    if (!isNewSession) return undefined

    const effectiveSessionId = sessionId ?? searchParams.get('session')
    if (!effectiveSessionId) {
      try {
        const result = await agentApi.sessions.createSession({ title: prompt, chatflowId })
        if (!result.error) {
          try {
            await mutate(`/sessions?chatflowId=${chatflowId}`)
            dispatchInference({ type: 'setCurrentSessionId', sessionId: result.data.id })
            updateSessionQueryParam(result.data.id)
            return result.data.id
          } catch (err) {
            console.error('Error mutating sessions', err)
          }
        }
      } catch (err) {
        console.error('Error creating new session', err)
      }
    } else {
      try {
        const result = await agentApi.sessions.updateSession(effectiveSessionId, {
          title: prompt,
        })
        if (!result.error) {
          await mutate(`/sessions?chatflowId=${chatflowId}`)
        }
      } catch (err) {
        console.error('Error updating session title', err)
      }
    }
    return undefined
  }

  const handleNewSession = async () => {
    const { data, error } = await agentApi.sessions.createSession({ title: 'New Session', chatflowId })
    resetMessages()
    return { sessionId: data?.id, error }
  }

  const handleToolCallApprovalDecision = async (message: InternalMessage, decision: 'approved' | 'rejected', reason?: string) => {
    const effectiveSessionId = message.sessionId ?? sessionId ?? currentSessionId
    const messageId = message.toolCallMessageId ?? message.id

    if (!effectiveSessionId || !messageId) {
      toast.error('Unable to resolve tool approval')
      return
    }

    try {
      dispatchInference({ type: 'setSubmitting', submitting: true })
      let response: { data: ApiMessage }
      if (decision === 'approved') {
        response = await chatMessageApi.approveToolCall(chatflowId, effectiveSessionId, messageId)
      } else {
        response = await chatMessageApi.rejectToolCall(chatflowId, effectiveSessionId, messageId, reason)
      }

      const refreshed = await refetchMessages()
      const refreshedMessages = refreshed ?? messagesFromApi ?? []

      if (hasPendingToolApproval(refreshedMessages)) {
        dispatchInference({ type: 'setSubmitting', submitting: false })
        return
      }

      if (!response.data.approvalContinuationStarted) {
        dispatchInference({ type: 'setSubmitting', submitting: false })
      }
    } catch (error) {
      dispatchInference({ type: 'setSubmitting', submitting: false })
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        await refetchMessages()
        toast.error(
          error.response?.data?.message ??
            (typeof error.response?.data === 'string' ? error.response.data : 'Tool approvals are still pending')
        )
        return
      }

      console.error(error)
      toast.error('Failed to resolve tool approval')
    }
  }

  const handleCancelSubTeammate = async (message: InternalMessage, reason?: string) => {
    const effectiveSessionId = message.sessionId ?? sessionId ?? currentSessionId
    const messageId = message.toolCallMessageId ?? message.id

    if (!effectiveSessionId || !messageId) {
      toast.error('Unable to cancel sub-teammate')
      return
    }

    try {
      dispatchInference({ type: 'setSubmitting', submitting: true })
      const { data } = await chatMessageApi.cancelSubTeammateExecution(chatflowId, effectiveSessionId, messageId, reason)
      // Cancel can change server state without a useful SSE payload.
      await refetchMessages()
      if (!data.canceled) {
        dispatchInference({ type: 'setSubmitting', submitting: false })
      }
    } catch (error) {
      dispatchInference({ type: 'setSubmitting', submitting: false })
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        toast.error(
          error.response?.data?.message ??
            (typeof error.response?.data === 'string' ? error.response.data : 'Unable to cancel sub-teammate')
        )
        return
      }
      console.error(error)
      toast.error('Failed to cancel sub-teammate')
    }
  }

  const handleAcceptSubTeammate = async (message: InternalMessage) => {
    const effectiveSessionId = message.sessionId ?? sessionId ?? currentSessionId
    const messageId = message.toolCallMessageId ?? message.id

    if (!effectiveSessionId || !messageId) {
      toast.error('Unable to accept sub-teammate result')
      return
    }

    try {
      const { data } = await chatMessageApi.acceptSubTeammateResult(chatflowId, effectiveSessionId, messageId)
      if (!data.accepted) {
        await refetchMessages()
        return
      }
      clearSubTeammateAcceptanceState(messageId)
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        toast.error(
          error.response?.data?.message ??
            (typeof error.response?.data === 'string' ? error.response.data : 'Unable to accept sub-teammate result')
        )
        return
      }
      console.error(error)
      toast.error('Failed to accept sub-teammate result')
    }
  }

  const handleSubmitSubTeammateFollowUp = async (message: InternalMessage, followUp: string) => {
    const trimmed = followUp.trim()
    if (!trimmed) {
      toast.error('Enter a follow-up message')
      return
    }
    const effectiveSessionId = message.sessionId ?? sessionId ?? currentSessionId
    const messageId = message.toolCallMessageId ?? message.id

    if (!effectiveSessionId || !messageId) {
      toast.error('Unable to send follow-up')
      return
    }

    try {
      dispatchInference({ type: 'setSubmitting', submitting: true })
      const { data } = await chatMessageApi.submitSubTeammateFollowUp(chatflowId, effectiveSessionId, messageId, trimmed)
      if (!data.dispatched) {
        await refetchMessages()
        return
      }
      markSubTeammateRerunning(messageId)
    } catch (error) {
      dispatchInference({ type: 'setSubmitting', submitting: false })
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        toast.error(
          error.response?.data?.message ?? (typeof error.response?.data === 'string' ? error.response.data : 'Unable to send follow-up')
        )
        return
      }
      console.error(error)
      toast.error('Failed to send follow-up')
    }
  }

  const submitMessageFeedback = async ({ messageId, text, value }: SubmitMessageFeedbackRequest): Promise<boolean> => {
    const normalizedText = text?.trim() || null

    if (!normalizedText && !value) {
      toast.error('Add a comment or choose approve/disapprove before submitting feedback.')
      return false
    }

    try {
      await chatMessageApi.createMessageFeedback(messageId, {
        text: normalizedText,
        value: value ?? null,
        timestamp: new Date().toISOString(),
      })
      await refetchMessages()
      toast.success('Feedback submitted')
      return true
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        await refetchMessages()
        toast.error('Feedback has already been submitted for this message.')
        return false
      }

      console.error(error)
      toast.error('Failed to submit feedback')
      return false
    }
  }

  const setVideoEnabled = (enabled: boolean) => {
    dispatchInference({ type: 'setVideoEnabled', enabled })
  }

  const setVideoLoading = (loading: boolean) => {
    dispatchInference({ type: 'setVideoLoading', loading })
  }

  return {
    isConnected,
    handleSubmitMessage,
    loading: loading || isLoadingMessages,
    messages,
    currentSessionId,
    latestMessage,
    handleNewSession,
    videoEnabled,
    setVideoEnabled,
    videoLink,
    videoLoading,
    setVideoLoading,
    clearMessages,
    approveToolCall: (message: InternalMessage) => handleToolCallApprovalDecision(message, 'approved'),
    rejectToolCall: (message: InternalMessage, reason?: string) => handleToolCallApprovalDecision(message, 'rejected', reason),
    cancelSubTeammate: handleCancelSubTeammate,
    acceptSubTeammate: handleAcceptSubTeammate,
    submitSubTeammateFollowUp: handleSubmitSubTeammateFollowUp,
    submitMessageFeedback,
  }
}

function hasPendingToolApproval(messages: ApiMessage[]) {
  return messages.some(message => {
    const content = getToolCallContent(message)
    return message.pendingApproval === true || content?.approval?.status === 'pending'
  })
}
